import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Printer, RefreshCw,
  CheckCircle, AlertTriangle, Loader2, TestTube,
  Smartphone,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { AdminNavbar } from '../../components/custom/AdminNavbar';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import printerService, {
  getStatusForMode,
  testPrintWithMode,
} from '../../services/printer/printerService';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MODE = 'android_builtin';

const AdminPrinterSettings = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [autoPrint, setAutoPrint] = useState(true);

  // Built-in POS printer status
  const [status, setStatus] = useState({ available: false, deviceName: null, error: null });

  useEffect(() => {
    if (!user || !user.is_admin) {
      navigate('/admin/login');
      return;
    }
    loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);

  const loadSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/admin/printer-settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAutoPrint(response.data.auto_print !== false);
      // Always force Android built-in mode into the printerService — QZ Tray
      // path is retired.
      printerService.setMode(MODE);
    } catch (error) {
      console.error('Failed to load printer settings:', error);
    } finally {
      setLoading(false);
      probeStatus();
    }
  };

  const probeStatus = useCallback(async () => {
    const s = await getStatusForMode(MODE);
    setStatus(s);
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/admin/printer-settings`,
        {
          printer_name: null,
          auto_print: autoPrint,
          printer_mode: MODE,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Printer settings saved');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestPrint = async () => {
    setTesting(true);
    try {
      const result = await testPrintWithMode(MODE);
      if (result.success) {
        toast.success('Test receipt printed!');
      } else {
        toast.error(`Print failed: ${result.error}`);
      }
    } catch (error) {
      console.error('Test print failed:', error);
      toast.error(`Print failed: ${error.message}`);
    } finally {
      setTesting(false);
    }
  };

  if (!user || !user.is_admin) return null;

  return (
    <div className="min-h-screen bg-background">
      <AdminNavbar title="Printer Settings" />
      <div className="max-w-2xl mx-auto px-4 md:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <Link to="/admin/dashboard">
            <Button variant="ghost">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Button>
          </Link>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
            <p className="text-muted-foreground mt-2">Loading settings...</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Built-in POS Printer Status */}
            <motion.div
              className="bg-white rounded-2xl p-6 shadow-sm"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h2 className="font-bold text-lg mb-4 flex items-center gap-2">
                <Smartphone className="h-5 w-5" />
                Built-in POS Printer
              </h2>

              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  {status.available ? (
                    <>
                      <div className="h-3 w-3 rounded-full bg-green-500 animate-pulse" />
                      <div>
                        <div className="text-green-700 font-medium" data-testid="printer-status">
                          Connected
                        </div>
                        {status.deviceName && (
                          <div className="text-xs text-muted-foreground">{status.deviceName}</div>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="h-3 w-3 rounded-full bg-amber-500" />
                      <div>
                        <div className="text-amber-700 font-medium" data-testid="printer-status">
                          Built-in Printer Unavailable
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {status.error || 'No Android printer bridge detected on this device'}
                        </div>
                      </div>
                    </>
                  )}
                </div>
                <Button variant="ghost" size="sm" onClick={probeStatus}>
                  <RefreshCw className="h-4 w-4 mr-1" />
                  Recheck
                </Button>
              </div>

              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                <AlertTriangle className="h-4 w-4 inline mr-2 text-blue-600" />
                Prints go to the Android POS device&apos;s built-in thermal printer. Install the
                Cravin POS wrapper app on the device to enable printing. No IP address, no QZ
                Tray, no laptop required.
              </div>
            </motion.div>

            {/* Auto-print toggle */}
            <motion.div
              className="bg-white rounded-2xl p-6 shadow-sm"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h2 className="font-bold text-lg mb-4">Print Settings</h2>
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="auto-print" className="font-medium">Auto-print new orders</Label>
                  <p className="text-sm text-muted-foreground">
                    Automatically print a receipt when a new paid order arrives
                  </p>
                </div>
                <Switch
                  id="auto-print"
                  checked={autoPrint}
                  onCheckedChange={setAutoPrint}
                  data-testid="auto-print-toggle"
                />
              </div>
            </motion.div>

            {/* Actions */}
            <motion.div
              className="bg-white rounded-2xl p-6 shadow-sm"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h2 className="font-bold text-lg mb-4">Actions</h2>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  variant="outline"
                  onClick={handleTestPrint}
                  disabled={testing}
                  className="flex-1"
                  data-testid="test-print-button"
                >
                  {testing ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Printing...
                    </>
                  ) : (
                    <>
                      <TestTube className="h-4 w-4 mr-2" />
                      Test Print
                    </>
                  )}
                </Button>

                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex-1 bg-primary"
                  data-testid="save-settings-button"
                >
                  {saving ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Save Settings
                    </>
                  )}
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPrinterSettings;
